import React, { FunctionComponent } from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { GetResponsiveWidth } from '../common/TabsComponent';
import { useResponsive } from '../../contexts/ResponsiveContext';

interface DistributorLocationCardProps {
    distributorLocationCards: DistributorLocationCard[];
    isRegional: boolean;
}

const commonTextStyle = {
    fontFamily: 'Poppins',
    fontWeight: 300,
    fontSize: '15px',
    lineHeight: '20px',
};

interface DistributorLocationCard {
    title: string;
    region?: string | string[];
    country?: string | string[];
    url?: string;
    logo?: string;
    freeformText1?: string;
}

const DistributorLocationCard: FunctionComponent<DistributorLocationCardProps> = ({
    distributorLocationCards,
    isRegional,
}) => {
    const { isScreenXl, isScreenLg, isScreenMd } = useResponsive();

    return isRegional ? (
        <Box
            data-testid="distributor-location-zone"
            sx={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'space-around',
                width: GetResponsiveWidth,
                boxSizing: 'border-box',
                flexDirection: 'row',
                overflowY: 'hidden',
                backgroundColor: 'white',
                mb: '1rem',
            }}
        >
            {distributorLocationCards.map((distributor, index) => {
                const countries = Array.isArray(distributor.country)
                    ? distributor.country
                    : distributor.country
                      ? distributor.country.split(',').map((c) => c.trim())
                      : [];

                const displayedCountries =
                    countries.length > 3
                        ? `${countries.slice(0, 3).join(', ')}...`
                        : countries.join(', ');

                const hasFreeform = Boolean(distributor.freeformText1?.trim());

                return (
                    <Box
                        data-testid={`distributor-${distributor.title}`}
                        key={index}
                        component="a"
                        {...(distributor.url
                            ? {
                                  href: distributor.url,
                                  target: '_blank',
                                  rel: 'noopener noreferrer',
                              }
                            : {})}
                        sx={{
                            width: isScreenXl
                                ? '362px'
                                : isScreenLg || isScreenMd
                                  ? '282px'
                                  : '370px',
                            height: '180px',
                            bgcolor: 'white',
                            border: '1px solid #e0e0e0',
                            borderRadius: '1px',
                            boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                            mb: '1rem',
                            p: '0.6rem 0.4rem 0.2rem 0.6rem',
                            boxSizing: 'border-box',
                            display: 'flex',
                            flexDirection: 'column', // 直向排列
                            gap: '0.25rem', // 元素之間留一點距離
                            textDecoration: 'none',
                            transition: 'box-shadow .3s ease-in-out',
                            '&:hover': { boxShadow: '0 8px 16px rgba(0,0,0,0.2)' },
                        }}
                    >
                        {/* 標題 */}
                        <Typography
                            sx={{
                                fontFamily: 'Poppins',
                                color: '#C30101',
                                fontSize: '16px',
                                fontWeight: 500,
                            }}
                        >
                            {distributor.title}
                        </Typography>

                        {/* 文字資訊：按順序自然流動，不用 absolute */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
                            {distributor.region && countries?.length > 1 ? (
                                <Typography sx={{ ...commonTextStyle, color: '#5A5A5A' }}>
                                    {Array.isArray(distributor.region)
                                        ? distributor.region.join(', ')
                                        : distributor.region}
                                </Typography>
                            ) : null}

                            {hasFreeform && (
                                <Typography sx={{ ...commonTextStyle, color: '#5A5A5A' }}>
                                    {distributor.freeformText1}
                                </Typography>
                            )}

                            {countries.length > 0 &&
                                (countries.length > 3 ? (
                                    <Tooltip
                                        title={
                                            Array.isArray(distributor.country)
                                                ? distributor.country.join(', ')
                                                : distributor.country || ''
                                        }
                                        componentsProps={{
                                            tooltip: {
                                                sx: { fontSize: '16px', fontFamily: 'Poppins' },
                                            },
                                        }}
                                    >
                                        <Typography sx={{ ...commonTextStyle, color: '#5A5A5A' }}>
                                            {displayedCountries}
                                        </Typography>
                                    </Tooltip>
                                ) : (
                                    <Typography sx={{ ...commonTextStyle, color: '#5A5A5A' }}>
                                        {displayedCountries}
                                    </Typography>
                                ))}
                        </Box>

                        {/* Logo 壓到底部、靠右 */}
                        {distributor.logo && (
                            <Box
                                data-testid={`${distributor.title}-image-container`}
                                sx={{
                                    mt: 'auto',
                                    alignSelf: 'flex-end',
                                    width: 151,
                                    height: 85,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'flex-end',
                                    overflow: 'hidden',
                                }}
                            >
                                <Box
                                    data-testid={`${distributor.title}-image`}
                                    component="img"
                                    src={distributor.logo}
                                    alt={`${distributor.title}-image`}
                                    sx={{
                                        maxWidth: '100%',
                                        maxHeight: '100%',
                                        width: '100%', // 或拿掉，搭配 maxWidth/maxHeight 也可
                                        height: '100%', // 同上
                                        objectFit: 'contain', // 等比縮放，完整顯示在框內
                                        display: 'block', // 消除 baseline 空隙
                                    }}
                                />
                            </Box>
                        )}
                    </Box>
                );
            })}
        </Box>
    ) : (
        <Box
            data-testid="distributor-location-zone"
            sx={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'space-around',
                padding: '0.2rem',
                width: '100%',
                boxSizing: 'border-box',
                flexDirection: 'row',
                overflowY: 'hidden',
                backgroundColor: 'white',
                marginBottom: '1rem',
            }}
        >
            {distributorLocationCards.map((distributor, index) => (
                <Box
                    data-testid={`distributor-${distributor.title}`}
                    key={index}
                    component="a"
                    href={distributor.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                        width: '134px',
                        height: '128px',
                        backgroundColor: 'white',
                        border: '1px solid #e0e0e0',
                        borderRadius: '2px',
                        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                        margin: '0.3rem',
                        padding: '0.4rem',
                        boxSizing: 'border-box',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textDecoration: 'none',
                        '&:hover': {
                            boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2)',
                        },
                    }}
                >
                    <Box
                        sx={{
                            width: '120px',
                            height: '100px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                        }}
                    >
                        <Box sx={{ flexGrow: 1 }} />
                        <Box
                            component="img"
                            src={distributor.logo}
                            alt="Logo"
                            sx={{ maxWidth: '120px', maxHeight: '80px' }}
                        />
                        <Box sx={{ flexGrow: 1 }} />
                    </Box>
                    <Typography
                        sx={{
                            ...commonTextStyle,
                            marginTop: 'auto',
                            color: '#7D7D7D',
                            textAlign: 'center',
                        }}
                    >
                        {distributor.title}
                    </Typography>
                </Box>
            ))}
        </Box>
    );
};

export default DistributorLocationCard;
